import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { money, number, thaiDateTime } from '../lib/format'

/**
 * Revenue forecasting (part 84). The server owns the whole calculation: the page only chooses
 * a horizon, asks for a run, and renders the stored points. That split is deliberate — the
 * numbers on this screen must be the same numbers a second reviewer sees a week later, even
 * after new invoices have arrived, which is only true if the run is a stored snapshot.
 */

interface ForecastRun {
  id: string
  method: string
  window_months: number
  horizon_months: number
  history_from: string
  history_to: string
  months_with_invoices: number
  zero_months: number
  mrr_satang: number
  arr_satang: number
  slope_satang_per_month: number
  r2: number
  residual_std_satang: number
  confidence_pct: number
  seasonality_reliable: boolean
  generated_by?: string
  generated_at: string
}

interface ForecastPoint {
  month: string
  kind: 'history' | 'forecast'
  horizon: number
  invoices: number
  actual_satang?: number
  forecast_satang: number
  lower_satang: number
  upper_satang: number
  moving_average_satang?: number
  seasonality_index: number
}

interface ForecastRunView {
  run: ForecastRun
  history: ForecastPoint[]
  forecast: ForecastPoint[]
}

const TH_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']

function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number)
  if (!y || !m) return month
  return `${TH_MONTHS[m - 1]} ${String(y + 543).slice(2)}`
}

function fullMonth(month: string): string {
  const [y, m] = month.split('-').map(Number)
  if (!y || !m) return month
  return `${TH_MONTHS[m - 1]} ${y + 543}`
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function shortID(id: string): string {
  return id.slice(0, 8)
}

/** A compact line chart drawn directly in SVG: actual, fitted/forecast and the band. */
function ForecastChart({ history, forecast }: { history: ForecastPoint[]; forecast: ForecastPoint[] }) {
  if (history.length === 0) return null
  const series = [...history, ...forecast]
  const W = 820
  const H = 260
  const left = 62
  const right = 16
  const top = 18
  const bottom = 34
  const plotW = W - left - right
  const plotH = H - top - bottom
  const max = Math.max(1, ...series.map((p) => p.upper_satang))
  const x = (i: number) => left + (i * plotW) / Math.max(1, series.length - 1)
  const y = (v: number) => top + plotH - (v / max) * plotH

  const actual = history.map((p, i) => `${x(i)},${y(p.actual_satang ?? 0)}`).join(' ')
  const fitted = history.map((p, i) => `${x(i)},${y(p.forecast_satang)}`).join(' ')
  const lastActual = history.length > 0 ? y(history[history.length - 1].actual_satang ?? 0) : 0
  const projected = [
    `${x(history.length - 1)},${lastActual}`,
    ...forecast.map((p, i) => `${x(history.length + i)},${y(p.forecast_satang)}`),
  ].join(' ')
  const band = [
    ...forecast.map((p, i) => `${x(history.length + i)},${y(p.upper_satang)}`),
    ...[...forecast].reverse().map((p, i) => `${x(history.length + forecast.length - 1 - i)},${y(p.lower_satang)}`),
  ].join(' ')

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="กราฟรายได้จริงและพยากรณ์" style={{ width: '100%', height: 'auto' }}>
        {[0, 0.5, 1].map((share) => {
          const yy = top + plotH * (1 - share)
          return (
            <g key={share}>
              <line x1={left} y1={yy} x2={W - right} y2={yy} stroke="var(--color-hairline)" strokeDasharray="3 5" />
              <text x={left - 8} y={yy + 4} textAnchor="end" fontSize={10} fill="var(--color-ivory-dim)">
                {money(Math.round(max * share), { compact: true })}
              </text>
            </g>
          )
        })}

        {forecast.length > 0 ? (
          <polygon points={band} fill="var(--color-emerald)" opacity={0.10} />
        ) : null}

        <polyline points={fitted} fill="none" stroke="var(--color-ivory-dim)" strokeWidth={1} strokeDasharray="2 4" opacity={0.7} />
        <polyline points={actual} fill="none" stroke="var(--color-gold)" strokeWidth={2.2} />
        {forecast.length > 0 ? (
          <polyline points={projected} fill="none" stroke="var(--color-emerald-soft)" strokeWidth={2.2} strokeDasharray="6 4" />
        ) : null}

        {history.map((p, i) => (
          <circle key={p.month} cx={x(i)} cy={y(p.actual_satang ?? 0)} r={2.4} fill="var(--color-gold)">
            <title>{`${fullMonth(p.month)}: ${money(p.actual_satang ?? 0)} (${number(p.invoices)} ใบแจ้งหนี้)`}</title>
          </circle>
        ))}
        {forecast.map((p, i) => (
          <circle key={p.month} cx={x(history.length + i)} cy={y(p.forecast_satang)} r={2.6} fill="var(--color-emerald-soft)">
            <title>{`${fullMonth(p.month)}: ${money(p.forecast_satang)} · ช่วง ${money(p.lower_satang)} – ${money(p.upper_satang)}`}</title>
          </circle>
        ))}

        {series.map((p, i) =>
          i % 2 === 0 || i === series.length - 1 ? (
            <text key={`x-${p.month}`} x={x(i)} y={H - 12} textAnchor="middle" fontSize={10} fill="var(--color-ivory-dim)">
              {monthLabel(p.month)}
            </text>
          ) : null,
        )}
      </svg>
      <div className="row" style={{ gap: 18, marginTop: 10, fontSize: 11.5 }}>
        <span className="row" style={{ gap: 6 }}>
          <i style={{ width: 16, height: 2, background: 'var(--color-gold)', display: 'inline-block' }} />
          <span className="dim">รายได้จริง</span>
        </span>
        <span className="row" style={{ gap: 6 }}>
          <i style={{ width: 16, height: 2, background: 'var(--color-emerald-soft)', display: 'inline-block' }} />
          <span className="dim">พยากรณ์</span>
        </span>
        <span className="row" style={{ gap: 6 }}>
          <i style={{ width: 14, height: 8, background: 'var(--color-emerald)', opacity: 0.16, display: 'inline-block' }} />
          <span className="dim">ช่วงเชื่อมั่น 95%</span>
        </span>
      </div>
    </div>
  )
}

export function Forecast() {
  const qc = useQueryClient()
  const [horizon, setHorizon] = useState(3)
  const [selectedID, setSelectedID] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const runs = useQuery({
    queryKey: ['forecast-runs'],
    queryFn: () => list<ForecastRun>('/v1/forecast/runs'),
    staleTime: 30_000,
  })

  const active = runs.data?.find((r) => r.id === selectedID) ?? runs.data?.[0]

  const detail = useQuery({
    queryKey: ['forecast-run', active?.id],
    queryFn: () => api<ForecastRunView>(`/v1/forecast/runs/${active?.id}/points`),
    enabled: Boolean(active?.id),
    staleTime: 60_000,
  })

  const create = useMutation({
    mutationFn: () => apiWrite<ForecastRunView>('/v1/forecast/runs', 'POST', { horizon_months: horizon }),
    onSuccess: (view) => {
      // Put the new snapshot in the cache before the list refetch so the chart updates in the
      // same render as the success message, instead of flashing the previous run.
      qc.setQueryData(['forecast-run', view.run.id], view)
      qc.setQueryData<ForecastRun[]>(['forecast-runs'], (old) => [
        view.run,
        ...(old ?? []).filter((r) => r.id !== view.run.id),
      ])
      setSelectedID(view.run.id)
      setNotice(`สร้างพยากรณ์ ${view.run.horizon_months} เดือน เมื่อ ${thaiDateTime(view.run.generated_at)} แล้ว`)
      void qc.invalidateQueries({ queryKey: ['forecast-runs'] })
    },
  })

  const run = detail.data?.run
  const history = detail.data?.history ?? []
  const forecast = detail.data?.forecast ?? []
  const next = forecast[0]
  const busy = create.isPending

  return (
    <>
      <PageHead
        title="พยากรณ์รายได้"
        sub="คำนวณจากใบแจ้งหนี้ที่ออกแล้ว 12 เดือนล่าสุด · ค่าเฉลี่ยเคลื่อนที่ 3 เดือน · เส้นแนวโน้มกำลังสองน้อยที่สุด · ดัชนีฤดูกาล · ช่วงเชื่อมั่น 95%"
        actions={
          <div className="row" style={{ gap: 8 }}>
            <select
              className="select"
              aria-label="จำนวนเดือนล่วงหน้า"
              value={horizon}
              onChange={(e) => setHorizon(Number(e.target.value))}
              style={{ width: 132, height: 34 }}
              disabled={busy}
            >
              <option value={3}>ล่วงหน้า 3 เดือน</option>
              <option value={6}>ล่วงหน้า 6 เดือน</option>
              <option value={12}>ล่วงหน้า 12 เดือน</option>
            </select>
            <button
              className="btn btn-primary btn-sm"
              type="button"
              disabled={busy}
              onClick={() => {
                setNotice(null)
                create.mutate()
              }}
            >
              {busy ? 'กำลังคำนวณ…' : 'สร้างพยากรณ์ใหม่'}
            </button>
          </div>
        }
      />

      {notice ? (
        <div
          role="status"
          style={{
            marginTop: 4,
            marginBottom: 16,
            padding: '10px 14px',
            fontSize: 12.5,
            border: '1px solid var(--color-emerald-soft)',
            borderRadius: 6,
            color: 'var(--color-emerald-soft)',
          }}
        >
          {notice}
        </div>
      ) : null}

      {create.isError ? (
        <div className="form-error" style={{ marginBottom: 16 }}>
          {errorText(create.error, 'สร้างพยากรณ์ไม่สำเร็จ')}
        </div>
      ) : null}

      {runs.isLoading ? (
        <div className="stack"><Skel h={110} /><Skel h={260} /><Skel h={180} /></div>
      ) : runs.isError ? (
        <div className="card card-pad">
          <div className="form-error">{errorText(runs.error, 'โหลดรายการพยากรณ์ไม่สำเร็จ')}</div>
          <p className="dim" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
            ตรวจสอบว่าเส้นทาง /v1/forecast/runs ถูก mount แล้ว หรือยังไม่มีใบแจ้งหนี้ที่ออกแล้วในระบบ
          </p>
        </div>
      ) : (runs.data ?? []).length === 0 ? (
        <div className="card card-pad">
          <Empty
            title="ยังไม่มีการพยากรณ์"
            body="เลือกจำนวนเดือนล่วงหน้า แล้วกดสร้างพยากรณ์ ระบบจะสรุปยอดจากใบแจ้งหนี้จริงทั้ง 12 เดือนให้ที่นี่"
          />
        </div>
      ) : detail.isLoading ? (
        <div className="stack"><Skel h={110} /><Skel h={260} /></div>
      ) : detail.isError ? (
        <div className="card card-pad">
          <div className="form-error">{errorText(detail.error, 'โหลดจุดพยากรณ์ไม่สำเร็จ')}</div>
        </div>
      ) : !run || !next ? (
        <div className="card card-pad">
          <Empty title="พยากรณ์นี้ไม่มีจุดข้อมูล" body="ลองสร้างพยากรณ์ใหม่หลังมีใบแจ้งหนี้ที่ออกแล้วอย่างน้อยหนึ่งใบ" />
        </div>
      ) : (
        <>
          <div className="grid-4 stagger">
            <Stat
              label="MRR"
              value={money(run.mrr_satang, { compact: true })}
              foot="ฐานรายได้ประจำของเดือนล่าสุดที่ออกใบแจ้งหนี้"
            />
            <Stat
              label="ARR"
              value={money(run.arr_satang, { compact: true })}
              foot="MRR × 12 · ยังไม่รวมส่วนเกินที่ผันแปร"
            />
            <Stat
              label={`คาดการณ์ ${fullMonth(next.month)}`}
              value={money(next.forecast_satang, { compact: true })}
              foot={`ช่วง ${money(next.lower_satang, { compact: true })} – ${money(next.upper_satang, { compact: true })}`}
            />
            <Stat
              label="ความเชื่อมั่น"
              value={number(run.confidence_pct)}
              unit="%"
              foot={`R² ${run.r2.toFixed(2)} · ส่วนเบี่ยงเบน ${money(run.residual_std_satang, { compact: true })}`}
            />
          </div>

          {run.months_with_invoices < 3 ? (
            <div
              style={{
                marginTop: 14,
                padding: '10px 14px',
                fontSize: 12.5,
                border: '1px solid rgba(199, 123, 43, 0.35)',
                borderRadius: 6,
                color: 'var(--color-amber)',
              }}
            >
              ประวัติที่มีใบแจ้งหนี้มีเพียง {number(run.months_with_invoices)} เดือน จาก {number(run.window_months)} เดือน
              — ช่วงเชื่อมั่นจะกว้างและแนวโน้มยังไม่นิ่ง ควรใช้ดูทิศทางมากกว่ายึดเป็นตัวเลขผูกพัน
            </div>
          ) : null}

          <div className="grid-main" style={{ marginTop: 16 }}>
            <div className="card card-pad">
              <div className="row" style={{ alignItems: 'baseline' }}>
                <span className="card-label">รายได้จริงและพยากรณ์</span>
                <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                  {fullMonth(run.history_from)} – {fullMonth(forecast[forecast.length - 1].month)}
                </span>
              </div>
              <div style={{ marginTop: 16 }}>
                <ForecastChart history={history} forecast={forecast} />
              </div>
            </div>

            <div className="card card-pad">
              <div className="card-label">วิธีที่ใช้คำนวณ</div>
              <div className="mono" style={{ fontSize: 12, marginTop: 12, lineHeight: 1.7 }}>{run.method}</div>
              <dl style={{ margin: '14px 0 0', fontSize: 12, lineHeight: 2 }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">หน้าต่างข้อมูล</dt>
                  <dd style={{ margin: 0 }}>{number(run.window_months)} เดือน</dd>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">เดือนที่มีใบแจ้งหนี้</dt>
                  <dd style={{ margin: 0 }}>{number(run.months_with_invoices)}</dd>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">เดือนที่ไม่มีใบแจ้งหนี้</dt>
                  <dd style={{ margin: 0 }}>{number(run.zero_months)}</dd>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">ความชันต่อเดือน</dt>
                  <dd style={{ margin: 0 }}>{money(run.slope_satang_per_month, { compact: true })}</dd>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">ดัชนีฤดูกาล</dt>
                  <dd style={{ margin: 0 }}>{run.seasonality_reliable ? 'ใช้จริง' : 'ปิด (ข้อมูลไม่พอ)'}</dd>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">สร้างเมื่อ</dt>
                  <dd style={{ margin: 0 }}>{thaiDateTime(run.generated_at)}</dd>
                </div>
              </dl>
              <p className="dim" style={{ fontSize: 11.5, lineHeight: 1.8, marginTop: 14, marginBottom: 0 }}>
                เดือนที่ไม่มีใบแจ้งหนี้ถูกนับเป็นศูนย์ ไม่ใช่ข้ามไป เพื่อให้ค่าเฉลี่ยเคลื่อนที่และเส้นแนวโน้มสะท้อนรายได้ที่หายไปจริง
                และตัวหารของค่าเฉลี่ยคือจำนวนเดือนที่มีในหน้าต่าง จึงไม่มีโอกาสหารด้วยศูนย์
              </p>
            </div>
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">จุดพยากรณ์</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                {number(forecast.length)} เดือนล่วงหน้า · ช่วงเชื่อมั่น {number(run.confidence_pct)}%
              </span>
            </div>
            <div className="table-wrap" style={{ marginTop: 14 }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th>เดือน</th>
                    <th style={{ textAlign: 'right' }}>พยากรณ์</th>
                    <th style={{ textAlign: 'right' }}>ขอบล่าง</th>
                    <th style={{ textAlign: 'right' }}>ขอบบน</th>
                    <th style={{ textAlign: 'right' }}>ดัชนีฤดูกาล</th>
                    <th>เทียบเดือนก่อน</th>
                  </tr>
                </thead>
                <tbody>
                  {forecast.map((p, i) => {
                    const previous = i === 0 ? history[history.length - 1]?.actual_satang ?? 0 : forecast[i - 1].forecast_satang
                    const delta = p.forecast_satang - previous
                    return (
                      <tr key={p.month} style={{ cursor: 'default' }}>
                        <td>{fullMonth(p.month)}</td>
                        <td className="num tnum">{money(p.forecast_satang)}</td>
                        <td className="num tnum dim">{money(p.lower_satang)}</td>
                        <td className="num tnum dim">{money(p.upper_satang)}</td>
                        <td className="num tnum">{p.seasonality_index.toFixed(2)}</td>
                        <td className={delta < 0 ? 'sev-critical' : 'sev-low'}>
                          {delta === 0 ? '—' : `${delta > 0 ? '+' : ''}${money(delta)}`}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">ประวัติที่ใช้คำนวณ</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                ค่าจริงเทียบเส้นแนวโน้มที่ฟิตได้ · ค่าเฉลี่ยเคลื่อนที่ 3 เดือน
              </span>
            </div>
            <div className="table-wrap" style={{ marginTop: 14 }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th>เดือน</th>
                    <th style={{ textAlign: 'right' }}>ใบแจ้งหนี้</th>
                    <th style={{ textAlign: 'right' }}>รายได้จริง</th>
                    <th style={{ textAlign: 'right' }}>เฉลี่ย 3 เดือน</th>
                    <th style={{ textAlign: 'right' }}>แนวโน้มที่ฟิต</th>
                    <th style={{ textAlign: 'right' }}>ดัชนีฤดูกาล</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((p) => (
                    <tr key={p.month} style={{ cursor: 'default' }}>
                      <td>
                        {fullMonth(p.month)}
                        {p.invoices === 0 ? <span className="chip" style={{ marginLeft: 8 }}>ไม่มีใบแจ้งหนี้</span> : null}
                      </td>
                      <td className="num tnum">{number(p.invoices)}</td>
                      <td className="num tnum">{money(p.actual_satang ?? 0)}</td>
                      <td className="num tnum dim">{p.moving_average_satang === undefined ? '—' : money(p.moving_average_satang)}</td>
                      <td className="num tnum dim">{money(p.forecast_satang)}</td>
                      <td className="num tnum">{p.seasonality_index.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">ประวัติการพยากรณ์</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                แต่ละรายการเป็น snapshot ที่ไม่ถูกแก้ย้อนหลัง
              </span>
            </div>
            <div className="table-wrap" style={{ marginTop: 14 }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th>รหัส</th>
                    <th>สร้างเมื่อ</th>
                    <th>หน้าต่าง</th>
                    <th style={{ textAlign: 'right' }}>ล่วงหน้า</th>
                    <th style={{ textAlign: 'right' }}>MRR</th>
                    <th style={{ textAlign: 'right' }}>ARR</th>
                    <th style={{ textAlign: 'right' }}>เดือนว่าง</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(runs.data ?? []).map((r) => (
                    <tr key={r.id} style={{ cursor: 'default' }}>
                      <td className="mono">{shortID(r.id)}</td>
                      <td className="dim">{thaiDateTime(r.generated_at)}</td>
                      <td className="mono dim">{r.history_from} – {r.history_to}</td>
                      <td className="num tnum">{number(r.horizon_months)} เดือน</td>
                      <td className="num tnum">{money(r.mrr_satang, { compact: true })}</td>
                      <td className="num tnum">{money(r.arr_satang, { compact: true })}</td>
                      <td className="num tnum">{number(r.zero_months)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          type="button"
                          onClick={() => setSelectedID(r.id)}
                          disabled={r.id === run.id}
                        >
                          {r.id === run.id ? 'กำลังแสดง' : 'ดูจุด'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="card-label">ข้อจำกัดที่ควรรู้</div>
            <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
              <li>พยากรณ์จากยอดใบแจ้งหนี้ ไม่ใช่กระแสเงินสดรับ จึงไม่หักส่วนลด หนี้สูญ หรือกำหนดชำระ</li>
              <li>ดัชนีฤดูกาลต้องมีอย่างน้อย 6 เดือนที่มีทั้งรายได้จริงและเส้นแนวโน้มเป็นบวก มิฉะนั้นระบบปิดฤดูกาลเป็น 1.0 ทั้งปี</li>
              <li>เส้นแนวโน้มเป็นกำลังสองน้อยที่สุดธรรมดา ไม่มีการถ่วงน้ำหนักเดือนล่าสุด จึงไวต่อเดือนผิดปกติ</li>
              <li>ช่วงเชื่อมั่นเป็นการประมาณปกติจากส่วนเบี่ยงเบนของ residual ไม่ใช่การจำลอง และแคบลงเมื่อข้อมูลนิ่ง</li>
              <li>งวดที่ไม่มีใบแจ้งหนี้ถือเป็นรายได้ศูนย์ในหน้าต่าง 12 เดือน ไม่ใช่ข้อมูลหาย</li>
            </ul>
          </div>
        </>
      )}
    </>
  )
}
